import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Subject, catchError, debounceTime, finalize, of, switchMap } from 'rxjs';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../../core/i18n/translation.service';
import type { AssetChargePreview } from '../../../../../core/models/assets/asset-charges.dto';
import { AssetChargesService } from '../../../../../core/services/asset-charges.service';
import { UserLookupSelectComponent } from '../../../../../shared/components/user-lookup-select/user-lookup-select.component';
import { BenefitsDialogComponent } from '../../benefits/shared/dialogs/benefits-dialog/benefits-dialog.component';
import { AssetChargePreviewComponent } from '../asset-charge-preview/asset-charge-preview.component';
import { AssetSelectComponent } from '../asset-select/asset-select.component';

const PREVIEW_DEBOUNCE_MS = 300;

/** HR charges an employee for an item they bought (a purchase charge), optionally tied to an asset. */
@Component({
  selector: 'app-purchase-charge-dialog',
  standalone: true,
  imports: [TranslatePipe, ButtonModule, BenefitsDialogComponent, UserLookupSelectComponent, AssetSelectComponent, AssetChargePreviewComponent],
  templateUrl: './purchase-charge-dialog.component.html',
  styleUrl: './purchase-charge-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseChargeDialogComponent implements OnInit {
  private readonly charges = inject(AssetChargesService);
  private readonly translation = inject(Translation);
  private readonly destroyRef = inject(DestroyRef);
  private readonly previewTrigger = new Subject<void>();

  readonly created = output<void>();
  readonly closed = output<void>();

  protected readonly userId = signal('');
  protected readonly item = signal('');
  protected readonly assetId = signal('');
  protected readonly amountText = signal('');
  protected readonly payment = signal<'full' | 'installments'>('full');
  protected readonly countText = signal('2');
  protected readonly start = signal('');
  protected readonly startTouched = signal(false);
  protected readonly note = signal('');
  protected readonly preview = signal<AssetChargePreview | null>(null);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly amount = computed(() => Number(this.amountText()));
  protected readonly installments = computed(() => (this.payment() === 'full' ? 1 : Number(this.countText())));
  protected readonly countValid = computed(() => Number.isInteger(this.installments()) && this.installments() >= (this.payment() === 'full' ? 1 : 2));
  // WN-1510: the first deduction month is required (the server would silently fall back to the current period).
  protected readonly startValid = computed(() => !!this.start().trim());
  protected readonly startInvalid = computed(() => this.startTouched() && !this.startValid());
  protected readonly valid = computed(() => !!this.userId() && !!this.item().trim() && this.amount() > 0 && this.countValid() && this.startValid());

  constructor() {
    this.previewTrigger
      .pipe(
        debounceTime(PREVIEW_DEBOUNCE_MS),
        switchMap(() => {
          if (!this.userId() || !(this.amount() > 0) || !this.countValid()) return of(null);
          return this.charges.preview(this.userId(), this.amount(), this.installments()).pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.preview.set(result));
  }

  ngOnInit(): void {
    this.charges
      .currentPeriod()
      .pipe(catchError(() => of('')), takeUntilDestroyed(this.destroyRef))
      .subscribe((period) => {
        if (!this.start()) this.start.set(period.trim().replace(/^"|"$/g, ''));
      });
  }

  protected setUser(id: string): void {
    this.userId.set(id);
    this.queuePreview();
  }

  protected setStart(value: string): void {
    this.start.set(value);
    this.startTouched.set(true);
  }

  protected setAmount(value: string): void {
    this.amountText.set(value);
    this.queuePreview();
  }

  protected setPayment(value: 'full' | 'installments'): void {
    this.payment.set(value);
    this.queuePreview();
  }

  protected setCount(value: string): void {
    this.countText.set(value);
    this.queuePreview();
  }

  protected confirm(): void {
    if (!this.valid() || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    this.charges
      .createPurchase({
        userId: this.userId(),
        itemDescription: this.item().trim(),
        assetId: this.assetId() || null,
        amount: this.amount(),
        installments: this.installments(),
        startPayrollPeriodKey: this.start().trim(),
        note: this.note().trim() || null,
      })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => this.created.emit(),
        error: (err) => this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation))),
      });
  }

  private queuePreview(): void {
    this.preview.set(null);
    this.previewTrigger.next();
  }
}
